import type React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import { NavBar } from "@/components/assessment/NavBar";
import { Footer } from "@/components/assessment/Footer";
import { listPublishedPosts, type PostSummary } from "@/server/posts.functions";

const mono = "'JetBrains Mono', 'Space Mono', monospace";
const serif = "'EB Garamond', serif";
const sans = "'Work Sans', system-ui, sans-serif";

const CATEGORIES = ["whitepaper", "analysis", "explainer"] as const;
type Category = (typeof CATEGORIES)[number];

const CATEGORY_LABEL: Record<Category, string> = {
  whitepaper: "INTEL",
  analysis: "ANALYSIS",
  explainer: "RECON",
};

const searchSchema = z.object({
  category: z.enum(CATEGORIES).optional().catch(undefined),
  tag: z.string().min(1).max(64).optional().catch(undefined),
});

const postsQO = queryOptions({
  queryKey: ["blog-posts", "published"],
  queryFn: () => listPublishedPosts(),
});

export const Route = createFileRoute("/blog/")({
  validateSearch: zodValidator(searchSchema),
  loader: ({ context }) => context.queryClient.ensureQueryData(postsQO),
  head: () => ({
    meta: [
      { title: "Signals & Noise — LLM-DX" },
      {
        name: "description",
        content:
          "A personal field journal on the intersection of large language models and cybersecurity.",
      },
      { property: "og:title", content: "Signals & Noise — LLM-DX" },
      {
        property: "og:description",
        content:
          "A personal field journal on the intersection of large language models and cybersecurity.",
      },
      { property: "og:url", content: "https://llm-dx.io/blog" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Signals & Noise — LLM-DX" },
    ],
    links: [{ rel: "canonical", href: "https://llm-dx.io/blog" }],
  }),
  errorComponent: ({ error }) => (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--blog-bg)" }}>
      <p style={{ color: "var(--blog-text-muted)", fontFamily: mono }}>
        // error: {error.message}
      </p>
    </div>
  ),
  notFoundComponent: () => null,
  component: BlogIndex,
});

function BlogIndex() {
  const { data } = useSuspenseQuery(postsQO);
  const { category, tag } = Route.useSearch();

  const all = data.posts;
  let filtered = category
    ? all.filter((p) => (p.category ?? "").toLowerCase() === category)
    : all;
  if (tag) {
    const t = tag.toLowerCase();
    filtered = filtered.filter((p) =>
      (p.tags ?? []).map((x) => x.toLowerCase()).includes(t),
    );
  }

  const featured =
    filtered.find((p) => p.featured) ?? filtered[0] ?? null;
  const rest = featured
    ? filtered.filter((p) => p.id !== featured.id)
    : [];

  return (
    <div style={{ minHeight: "100vh", background: "var(--blog-bg)" }}>
      <NavBar currentView="home" onNavigate={() => {}} onReset={() => {}} />
      <main className="max-w-5xl mx-auto px-6 md:px-12 pt-24 pb-16">
        {/* Header — Tactical Field Journal */}
        <header className="relative pl-6 md:pl-8 py-8 mb-10">
          {/* Left rail gradient */}
          <div
            className="absolute left-0 top-8 bottom-8 w-px"
            style={{
              background:
                "linear-gradient(to bottom, var(--blog-accent), rgba(92,255,158,0.1) 50%, transparent)",
            }}
            aria-hidden
          />

          {/* Top instrument row */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-10">
            <div className="space-y-2">
              <p
                className="uppercase"
                style={{
                  fontFamily: mono,
                  fontSize: "10px",
                  letterSpacing: "0.2em",
                  color: "var(--blog-accent)",
                  opacity: 0.6,
                }}
              >
                LOC_ID: 53.3498° N, 6.2603° W
              </p>
              <div className="flex items-center gap-3">
                <span
                  className="h-px w-8"
                  style={{ background: "var(--blog-accent)" }}
                />
                <span
                  className="uppercase"
                  style={{
                    fontFamily: mono,
                    fontSize: "11px",
                    fontWeight: 500,
                    letterSpacing: "0.3em",
                    color: "var(--blog-accent)",
                  }}
                >
                  Field Dispatch 0x01
                </span>
              </div>
            </div>

            <div className="flex items-center gap-5">
              <div
                className="flex flex-col items-end gap-1 uppercase"
                style={{
                  fontFamily: mono,
                  fontSize: "10px",
                  letterSpacing: "0.15em",
                  color: "var(--blog-text-muted)",
                }}
              >
                <span>CALLSIGN: FITZPATRICK</span>
                <span>STATUS: TRANSMITTING</span>
              </div>
              <div
                className="w-10 h-10 flex items-center justify-center relative"
                style={{ border: "1px solid var(--blog-elevated)" }}
                aria-hidden
              >
                <div
                  className="absolute inset-0.5"
                  style={{ border: "1px solid rgba(92,255,158,0.08)" }}
                />
                <div
                  className="w-1.5 h-1.5 animate-pulse"
                  style={{
                    background: "var(--blog-accent)",
                    boxShadow: "0 0 8px var(--blog-accent)",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="relative mb-8">
            <h1
              className="leading-none tracking-tight select-none"
              style={{
                fontFamily: serif,
                fontStyle: "italic",
                fontWeight: 500,
                color: "var(--blog-text)",
                fontSize: "clamp(3.5rem, 9vw, 7.5rem)",
              }}
            >
              Signals{" "}
              <span style={{ color: "rgba(92,255,158,0.35)" }}>&amp;</span>{" "}
              Noise
            </h1>

            <div
              className="hidden md:block absolute -top-3 -right-3 w-20 h-20 pointer-events-none"
              style={{
                borderTop: "1px solid rgba(92,255,158,0.18)",
                borderRight: "1px solid rgba(92,255,158,0.18)",
              }}
              aria-hidden
            />
          </div>

          {/* Subtitle + side telemetry */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-7">
              <p
                className="text-base md:text-lg leading-relaxed"
                style={{
                  fontFamily: mono,
                  fontWeight: 300,
                  color: "var(--blog-accent)",
                  opacity: 0.85,
                }}
              >
                Field notes from a practitioner working the seam between large
                language models and offensive security — what breaks, what holds,
                and what the next adversary is already learning.
                <span
                  className="inline-block w-2 h-4 ml-1 align-middle animate-pulse"
                  style={{ background: "var(--blog-accent)" }}
                  aria-hidden
                />
              </p>
            </div>

            <div
              className="md:col-span-5 pt-4 space-y-3"
              style={{ borderTop: "1px solid var(--blog-elevated)" }}
            >
              <div
                className="flex justify-between uppercase"
                style={{
                  fontFamily: mono,
                  fontSize: "10px",
                  letterSpacing: "0.15em",
                  color: "var(--blog-text-muted)",
                }}
              >
                <span>SIGNAL_INTEGRITY</span>
                <span>0.642</span>
              </div>
              <div
                className="w-full h-1 overflow-hidden"
                style={{ background: "var(--blog-elevated)" }}
                aria-hidden
              >
                <div
                  className="h-full"
                  style={{ width: "64%", background: "rgba(92,255,158,0.5)" }}
                />
              </div>
              <p
                className="italic leading-snug"
                style={{
                  fontFamily: mono,
                  fontSize: "10px",
                  letterSpacing: "0.05em",
                  color: "var(--blog-text-muted)",
                }}
              >
                SYSTEM_NOTE: Raw observations from the field. No corporate
                sanitization applied. Personal opinions only.
              </p>
            </div>
          </div>

          {/* Hairline rule with build tag */}
          <div className="mt-10 relative">
            <div
              className="h-px w-full"
              style={{
                background:
                  "linear-gradient(to right, var(--blog-accent), rgba(92,255,158,0.05) 60%, transparent)",
              }}
            />
            <div
              className="absolute right-0 -top-3 uppercase"
              style={{
                fontFamily: mono,
                fontSize: "9px",
                letterSpacing: "0.2em",
                color: "var(--blog-text-muted)",
                opacity: 0.6,
              }}
            >
              SIGINT_V3.0_STABLE
            </div>
          </div>
        </header>

        {/* Filter Bar */}
        <FilterBar active={category} />

        {/* Posts */}
        {filtered.length === 0 ? (
          <p
            className="mt-16"
            style={{ color: "var(--blog-text-muted)", fontFamily: mono, fontSize: "0.85rem" }}
          >
            // no_logs_found {category ? `--category=${category}` : ""}
          </p>
        ) : (
          <div className="mt-12 space-y-12">
            {featured && <FeaturedLead post={featured} />}
            {rest.length > 0 && <SecondaryGrid posts={rest} />}
          </div>
        )}

        {/* EOF footer */}
        <footer
          className="flex justify-between items-center pt-12 mt-16"
          style={{
            borderTop: "1px solid var(--blog-elevated)",
            fontFamily: mono,
          }}
        >
          <span
            className="uppercase"
            style={{ fontSize: "10px", color: "var(--blog-text-muted)", letterSpacing: "0.15em" }}
          >
            EOF — End of File
          </span>
          <div className="flex gap-4 items-center">
            <a
              href="/blog/rss.xml"
              className="uppercase"
              style={{
                fontSize: "11px",
                color: "var(--blog-accent)",
                textDecoration: "underline",
                letterSpacing: "0.1em",
              }}
            >
              [ RSS ]
            </a>
          </div>
        </footer>
      </main>
      <Footer />
    </div>
  );
}

function FilterBar({ active }: { active: Category | undefined }) {
  const labels: Array<{ key: Category | undefined; label: string }> = [
    { key: undefined, label: "ALL_LOGS" },
    { key: "whitepaper", label: CATEGORY_LABEL.whitepaper },
    { key: "analysis", label: CATEGORY_LABEL.analysis },
    { key: "explainer", label: CATEGORY_LABEL.explainer },
  ];
  return (
    <nav aria-label="Filter posts by category" className="flex flex-wrap gap-3">
      {labels.map((l) => {
        const isActive = (l.key ?? null) === (active ?? null);
        return (
          <Link
            key={l.label}
            to="/blog"
            search={{ category: l.key }}
            className="px-3 py-1 uppercase transition-colors"
            style={{
              fontFamily: mono,
              fontSize: "0.7rem",
              fontWeight: 700,
              color: isActive ? "var(--blog-bg)" : "var(--blog-text-dim)",
              background: isActive ? "var(--blog-accent)" : "transparent",
              border: `1px solid ${isActive ? "var(--blog-accent)" : "var(--blog-elevated)"}`,
              textDecoration: "none",
            }}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.borderColor = "var(--blog-accent)";
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.borderColor = "var(--blog-elevated)";
            }}
          >
            [ {l.label} ]
          </Link>
        );
      })}
    </nav>
  );
}

function formatDate(iso: string): string {
  return new Date(iso)
    .toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })
    .toUpperCase();
}

function FeaturedLead({ post }: { post: PostSummary }) {
  return (
    <article
      className="group relative p-8 md:p-12 transition-all"
      style={{
        background: "var(--blog-surface)",
        border: "1px solid var(--blog-elevated)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--blog-accent-dim)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--blog-elevated)";
      }}
    >
      {/* LED dot */}
      <div className="absolute top-0 right-0 p-4">
        <div
          className="w-2 h-2"
          style={{
            background: "var(--blog-accent)",
            boxShadow: "0 0 8px var(--blog-accent)",
            borderRadius: 1,
          }}
        />
      </div>
      <Link
        to="/blog/$slug"
        params={{ slug: post.slug }}
        style={{ textDecoration: "none", display: "block" }}
      >
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-4">
            <span
              className="uppercase px-2 py-0.5"
              style={{
                fontFamily: mono,
                fontSize: "10px",
                color: "var(--blog-accent)",
                border: "1px solid var(--blog-accent-dim)",
                letterSpacing: "0.15em",
              }}
            >
              Priority_Alpha
            </span>
            <time
              dateTime={post.published_at}
              className="uppercase"
              style={{
                fontFamily: sans,
                fontSize: "11px",
                color: "var(--blog-text-muted)",
                letterSpacing: "0.2em",
              }}
            >
              {formatDate(post.published_at)}
            </time>
            {post.category && (
              <span
                className="uppercase"
                style={{
                  fontFamily: mono,
                  fontSize: "10px",
                  color: "var(--blog-text-muted)",
                  letterSpacing: "0.15em",
                }}
              >
                · {CATEGORY_LABEL[post.category as Category] ?? post.category}
              </span>
            )}
          </div>
          <h2
            className="text-3xl md:text-5xl leading-tight transition-colors"
            style={{
              fontFamily: serif,
              color: "var(--blog-text)",
            }}
          >
            {post.title}
          </h2>
          {post.summary && (
            <p
              className="text-lg md:text-xl leading-relaxed"
              style={{
                fontFamily: serif,
                fontStyle: "italic",
                color: "var(--blog-text-dim)",
              }}
            >
              {post.summary}
            </p>
          )}
          <div
            className="uppercase pt-4 flex flex-wrap items-center gap-6"
            style={{
              fontFamily: sans,
              fontSize: "11px",
              color: "var(--blog-text-muted)",
              letterSpacing: "0.2em",
              borderTop: "1px solid var(--blog-elevated)",
            }}
          >
            {post.reading_time_minutes && <span>{post.reading_time_minutes} min read</span>}
            <span>Auth: Fitzpatrick</span>
          </div>
        </div>
      </Link>
    </article>
  );
}

function SecondaryGrid({ posts }: { posts: PostSummary[] }) {
  // Preserve series labels as mono dividers.
  const out: React.ReactNode[] = [];
  let prevSeries: string | null | undefined = undefined;
  posts.forEach((p, i) => {
    if (p.series && p.series !== prevSeries) {
      out.push(
        <div
          key={`series-${p.series}-${i}`}
          className="col-span-full uppercase flex items-center gap-3 pt-4"
          style={{
            fontFamily: mono,
            fontSize: "10px",
            color: "var(--blog-text-muted)",
            letterSpacing: "0.2em",
          }}
        >
          <span>// series · {p.series}</span>
          <span
            className="flex-1 h-px"
            style={{ background: "var(--blog-elevated)" }}
          />
        </div>,
      );
    }
    prevSeries = p.series ?? null;
    out.push(<PostCard key={p.id} post={p} />);
  });
  return <div className="grid md:grid-cols-2 gap-8">{out}</div>;
}

function PostCard({ post }: { post: PostSummary }) {
  return (
    <article
      className="transition-all"
      style={{
        background: "var(--blog-surface)",
        border: "1px solid var(--blog-elevated)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--blog-accent-dim)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--blog-elevated)";
      }}
    >
      <Link
        to="/blog/$slug"
        params={{ slug: post.slug }}
        className="block p-8"
        style={{ textDecoration: "none" }}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <time
              dateTime={post.published_at}
              className="uppercase"
              style={{
                fontFamily: sans,
                fontSize: "10px",
                color: "var(--blog-text-muted)",
                letterSpacing: "0.2em",
              }}
            >
              {formatDate(post.published_at)}
            </time>
            {post.category && (
              <span
                className="uppercase"
                style={{
                  fontFamily: mono,
                  fontSize: "10px",
                  color: "var(--blog-accent)",
                  opacity: 0.7,
                  letterSpacing: "0.15em",
                }}
              >
                · {CATEGORY_LABEL[post.category as Category] ?? post.category}
              </span>
            )}
          </div>
          <h3
            className="text-2xl leading-tight"
            style={{ fontFamily: serif, color: "var(--blog-text)" }}
          >
            {post.title}
          </h3>
          {post.summary && (
            <p
              className="text-sm leading-relaxed"
              style={{
                fontFamily: serif,
                color: "var(--blog-text-dim)",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {post.summary}
            </p>
          )}
          <div className="flex items-center justify-between pt-2">
            <span
              className="uppercase"
              style={{
                fontFamily: mono,
                fontSize: "10px",
                color: "var(--blog-accent)",
                letterSpacing: "0.15em",
              }}
            >
              // read_more
            </span>
            {post.reading_time_minutes && (
              <span
                className="uppercase"
                style={{
                  fontFamily: sans,
                  fontSize: "10px",
                  color: "var(--blog-text-muted)",
                  letterSpacing: "0.2em",
                }}
              >
                {post.reading_time_minutes} min
              </span>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}
